<script>
import {ConsentModel} from '@/model/consent'
import {CandidateModel} from '@/model/candidate'
import {microbaWarning} from '@/services/notificationServices'
import btVueButton from 'bt-vue-button'
import { Validator } from 'vee-validate'
import {TelephoneConfiguration} from '@/model/telephoneConfiguration'

Validator.extend('isValidPhoneNumber', {
  getMessage: field => {
    return `Field ${field} is invalid phone number.`
  },
  validate: (phoneNumber, [valid]) => {
    // Need to test if this if statement is needed
    if (!phoneNumber) {
      return true
    }
    return valid === 'true'
  }
})

export default {
  name: 'Consent',
  components: {
    btVueButton,
    ConsentModel
  },
  props: {
    candidate: {
      type: Object,
      default: null
    },
    consent: {
      type: Object,
      default: null
    },
    isTerminated: {
      type: Boolean,
      default: false
    },
    recruitmentId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      actionInProgress: null,
      isDisabled: null,
      finalState: null,
      consentData: null,
      candidateData: null,
      phoneProps: new TelephoneConfiguration({required: false}),
      validPhone: null
    }
  },
  mounted () {
    this.fetchConsentDetails()
  },
  watch: {
    consentData: {
      handler () {
        this.$emit('update:consent', this.consentData)
      },
      deep: true
    },
    consent: {
      handler () {
        if (this.consentData.id !== this.consent.id) {
          this.fetchConsentDetails()
        }
      },
      deep: true
    }
  },
  computed: {
    isAgreeConsent () {
      return this.consentData.userResponse === true
    },
    isDisagreeConsent () {
      return this.consentData.userResponse === false
    }
  },
  methods: {
    checkPhoneNumberValidity (payload) {
      this.validPhone = payload.isValid
      this.candidateData.phoneNumber = this.validPhone ? payload.number.e164 : payload.number.input
    },
    validate () {
      setTimeout(() => {
        this.actionInProgress = false
      }, 1000)
      this.$validator.validateAll().then(() => {
        if (this.errors.any()) {
          return
        }

        if (this.consentData.userResponse === null) {
          microbaWarning({text: 'Please select an option before submitting.'})
          return
        }

        this.finalState = 'success'
        this.$emit('update:candidate', this.candidateData)
        this.$emit('update:consent', this.consentData)
        this.$emit('saveConsentResponse')
        this.finalState = null
      })
    },
    fetchConsentDetails () {
      this.$store.dispatch('getRecruitmentConsent', {recruitmentId: this.recruitmentId, candidateId: this.candidate.id, consentId: this.consent.id})
      .then(response => {
        this.consentData = new ConsentModel(response.data)
        const {firstName, lastName, email, phoneNumber} = response.data
        this.candidateData = new CandidateModel({id: this.candidate.id, firstName, lastName, email, phoneNumber})
      })
      .catch(error => {
        console.log(error)
      })
    },
    setUserResponse (response) {
      this.consentData.userResponse = response
    },
    setActionInProgressAsFalse () {
      setTimeout(() => {
        this.actionInProgress = false
      }, 100)
    }
  }
}
</script>

<template>
  <div v-if="consentData">
    <h2 class="mb-4">{{consentData.title}}</h2>
    <div v-html="consentData.htmlContent" class="mt-4"></div>
    <div class="mt-4" v-if="candidateData">
      <h3>Personal Details</h3>
      <div class="row">
        <div class="form-group col col-md-6">
          <b-form-input
            type="text"
            required
            autocomplete="given-name"
            v-validate="'required'"
            maxlength="25"
            name="firstName"
            v-model="candidateData.firstName"
            :state="errors.has('firstName') ? false : null"
            placeholder="First name"
          ></b-form-input>
          <div
            class="invalid-feedback"
            v-show="errors.has('firstName')"
          >{{ errors.first('firstName') }}</div>
        </div>

        <div class="form-group col col-md-6">
          <b-form-input
            type="text"
            v-model="candidateData.lastName"
            autocomplete="family-name"
            required
            v-validate="'required'"
            maxlength="25"
            name="lastName"
            :state="errors.has('lastName') ? false : null"
            placeholder="Last name"
          ></b-form-input>
          <div
            class="invalid-feedback"
            v-show="errors.has('lastName')"
          >{{ errors.first('lastName') }}</div>
        </div>

        <div class="form-group col-md-6">
          <b-form-input
            type="email"
            v-model="candidateData.email"
            v-validate="'required|email'"
            autocomplete="email"
            maxlength="25"
            name="email"
            :state="errors.has('email') ? false : null"
            required
            :disabled="true"
            placeholder="Enter email"
          ></b-form-input>
          <div class="invalid-feedback" v-show="errors.has('email')">{{ errors.first('email') }}</div>
        </div>

        <div class="form-group col-md-6">
          <vue-tel-input
            v-model="candidateData.phoneNumber"
            v-bind="phoneProps"
            v-on:onInput="checkPhoneNumberValidity"
            v-on:validate="checkPhoneNumberValidity"
            v-validate="`isValidPhoneNumber:${validPhone}`"
            :class="{ 'is-invalid' : errors.has('phone')}"
            name="phone"
          ></vue-tel-input>
          <div class="invalid-feedback" v-show="errors.has('phone')">{{ errors.first('phone') }}</div>
        </div>
      </div>
      <!--.row -->
    </div>
    <embed
      v-if="consentData.pdfLink"
      :src="consentData.pdfLink"
      width="100%"
      height="750"
      class="d-none d-md-block my-4 embededPDF"
    />
    <div class="radio-buttons mt-4 pt-4">
      <div class="form-radio opt-in">
        <input
          type="radio"
          ref="agreeConsent"
          id="agreeConsent"
          name="userConsentResponse"
          :checked="isAgreeConsent"
          v-model="consentData.userResponse"
          value="true"
          @click="setUserResponse(true)"
        />
        <label for="agreeConsent">{{consentData.agreeLabel}}</label>
      </div>
      <div class="form-radio opt-out">
        <input
          type="radio"
          ref="disagreeConsent"
          id="disagreeConsent"
          name="userConsentResponse"
          :checked="isDisagreeConsent"
          v-model="consentData.userResponse"
          value="false"
          @click="setUserResponse(false)"
        />
        <label for="disagreeConsent">{{consentData.disagreeLabel}}</label>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <btVueButton
          btnClass="btn btn-primary btn-dynamic"
          :actionInProgress.sync="actionInProgress"
          :isDisabled="isDisabled"
          :btnFinalState="finalState"
          v-on:clicked="validate"
          :btnValue="consentData.nextConsentId ? 'Save & Continue' : 'Submit' "
        >
          <template #inProgress>
            <span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
          </template>
        </btVueButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.opt-in {
  border: 1px solid #ff8708;
  padding: 1rem 2rem;
  border-radius: 99em;
  label {
    margin: 0 auto;
    color: #ff8708;
  }
}

.opt-out {
  /*border: 1px solid #ff8708;*/
  padding: 1rem 2rem;
  border-radius: 99em;
  label {
    font-size: 0.94rem;
    margin: 0 auto;
    /*color: #ff8708;*/
  }
}
</style>





// WEBPACK FOOTER //
// src/components/Consent.vue